import { APITypesV2 } from "@cur8/api-client";
import { ReactComponent as Checkmark } from "assets/checkmark.svg";
import { ReactComponent as Chevron } from "assets/chevron.svg";
import { ReactComponent as Clock } from "assets/error-clock.svg";
import { ReactComponent as Error } from "assets/error.svg";
import { ReactComponent as Squircle } from "assets/squircle.svg";
import { parseDocument } from "lib/legal/parseDocument";
import { DateTime } from "luxon";
import { useLegalDocumentQuery } from "render/hooks/api/queries/useLegalDocumentQuery";
import { Skeleton } from "render/ui/presentation/Skeleton";
import { Typography } from "render/ui/presentation/Typography";
import { Card } from "render/ui/trigger/Card";
import { Trans } from "../../trans";
import styles from "./styles.module.sass";

type ConsentCardProps = {
  consent: APITypesV2.ConsentWithSignature;
  onOpenConsentClick: (consentId: string) => void;
};

export function ConsentCard({ consent, onOpenConsentClick }: ConsentCardProps) {
  const url = consent?.relevantSignature?.documentUrl;
  const { data: legalDoc } = useLegalDocumentQuery(url);

  if (consent.relevantSignature == null || legalDoc == null) {
    return;
  }

  const { relevantSignature } = consent;
  const { consentId, expired, revoked, optedOut } = relevantSignature;
  const verb = getVerb(relevantSignature);
  const date = getDate(relevantSignature);
  const { title, description } = parseDocument(legalDoc);
  const warning = expired || revoked || optedOut;

  const Icon = getIcon(
    expired ? "expired" : revoked || optedOut ? "revoked" : "agreed"
  );

  return (
    <Card onClick={() => onOpenConsentClick(consentId)}>
      <div className={styles.content}>
        <div className={styles.header}>
          <div
            className={styles.squircle}
            data-variant={warning ? "warning" : "subtle"}
          >
            <Squircle />
            <Icon className={styles.icon} aria-hidden />
          </div>
          <Typography variant="body-label-m">{title}</Typography>
          <Chevron className={styles.chevron} />
        </div>
        <Typography variant="body-s" color="subtle">
          {description}
        </Typography>
      </div>
      {verb && date ? (
        <Typography variant="eyebrow-m" color={warning ? "warning" : "subtle"}>
          <Trans.Status verb={verb} date={date} />
        </Typography>
      ) : (
        <div className={styles.skeleton}>
          <Skeleton />
        </div>
      )}
    </Card>
  );
}

function getIcon(statusState: "expired" | "revoked" | "agreed") {
  switch (statusState) {
    case "expired":
      return Clock;
    case "revoked":
      return Error;
    default:
      return Checkmark;
  }
}

function getVerb({ expired, revoked, optedOut }: APITypesV2.ConsentSummary) {
  if (expired) return <Trans.Expired />;
  if (revoked) return <Trans.Revoked />;
  if (optedOut) return <Trans.OptedOut />;
  return <Trans.Agreed />;
}

function getDate({
  expired,
  expiresAt,
  revokedAt,
  createdAt,
}: APITypesV2.ConsentSummary) {
  const date = expired ? expiresAt : revokedAt ?? createdAt;
  return DateTime.fromISO(date).toLocaleString(DateTime.DATE_MED);
}
